import React, { useState, useEffect } from 'react';
import { useAppSelector } from 'hooks/reduxHooks';
import { RootState } from 'redux-setup/store';
import {
  getRepositorySettings,
  listMyRepositories,
  listRepositories,
} from 'services/repositoryService';
import { Loader } from 'shared-components';
import { ImportModal } from './import';
import RepositoryList from './list';
import './style.scss';
import { IReliantRepositorySettings } from 'interface/repositoryInterface';
import { isEmpty } from 'lodash';
import { IRepository } from 'interface/repositoryInterface';
import { toast } from 'react-toastify';

export function RepositoryManager() {
  const organizationId = useAppSelector(
    (state: RootState) => state.organization.id,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [repositories, setRepositories] = useState<IRepository[]>([]);
  const [repoSettings, setRepoSettings] = useState<
    IReliantRepositorySettings[]
  >([]);
  const [fetchedRepositories, setFetchedRepositories] = useState<IRepository[]>(
    [],
  );

  const fetchRepositoriesFromGithub = async () => {
    setLoading(true);

    try {
      const response = await listRepositories({
        OrganizationID: organizationId,
      });

      if (response.status === 200 && response.data) {
        setRepositories(response.data.Result || []);
        setLoading(false);
      } else {
        toast.error('Failed to fetch repositories');
      }
    } catch (error) {
      console.error('Error fetching repositories:', error);
      toast.error('Failed to fetch repositories');
    } finally {
      setLoading(false);
    }
  };

  const fetchRepositories = async () => {
    setLoading(true);
    try {
      const response = await listMyRepositories({
        OrganizationID: organizationId,
      });

      if (response.status === 200 && response.data) {
        setFetchedRepositories(response.data.Result);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching repositories:', error);
      toast.error('Failed to fetch repositories');
    } finally {
      setLoading(false);
    }
  };

  const fetchRepoSettings = async () => {
    if (isEmpty(fetchedRepositories)) {
      return;
    }
    for (const repo of fetchedRepositories) {
      try {
        const response = await getRepositorySettings({
          RepositoryID: repo.ID,
        });
        if (response.status === 200 && response.data) {
          setRepoSettings(prev => [...prev, response.data]);
        }
      } catch (error) {
        console.error('Error running API for repo:', repo, error);
        toast.error('Failed to fetch repository settings');
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchRepositoriesFromGithub();
      await fetchRepositories();
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!isEmpty(fetchedRepositories)) {
      fetchRepoSettings();
    }
  }, [fetchedRepositories]);

  return (
    <div>
      {loading ? (
        <Loader loading={loading} />
      ) : (
        <div className="dashboard_filter row-full flex mb-4">
          <ImportModal
            repositories={repositories}
            fetchRepositories={fetchRepositories}
            fetchedRepositories={fetchedRepositories}
          />
        </div>
      )}
      {loading ? (
        <Loader loading={loading} />
      ) : (
        <RepositoryList
          fetchedRepositories={fetchedRepositories}
          fetchRepositories={fetchRepositories}
          repoSettings={repoSettings}
        />
      )}
    </div>
  );
}
